<template>
  <v-card class="pa-4">
    <v-dialog v-model="showValidationDialog">
      <v-card class="search-modal">
        <h2>Validation Results</h2>
        <v-form class="search-form">
          <v-alert v-if="!!validationResult && !!validationResult.errors && validationResult.errors.length > 0" type="error">
            <pre>{{ validationResult.errors.join("\r\n") }}</pre>
          </v-alert>
          <v-alert v-if="!!validationResult && validationResult.warnings && validationResult.warnings.length > 0" type="warning" outlined>
            <pre>{{ validationResult.warnings.join("\r\n") }}</pre>
          </v-alert>
          <v-btn
            large
            color="primary"
            width="125px"
            :disabled="!validationResult.canBeSaved"
            v-on:click="saveChanges">
            <v-icon v-if="!validationResult.canBeSaved">mdi-alert</v-icon>
            Save
          </v-btn>
        </v-form>
      </v-card>
    </v-dialog>
    <v-row class="mb-3">
      <h3 v-if="personId">Person Details - {{personId}}</h3>
      <h3 v-else>Create New Person</h3>
    </v-row>
    <br />
    <div class="mt-3">
      <v-row>
        <v-textarea
          :disabled="!isEditMode"
          :visible="!isEditMode"
          outlined
          auto-grow
          clearable
          rows="1"
          v-model="personInfo.adminNotes"
          label="Description of changes (required)" />
        <v-btn
          class="ml-1 mr-1"
          :disabled="isEditMode && (!personInfo.adminNotes || /^\s*$/.test(personInfo.adminNotes))"
          color="primary"
          @click="togglePersonEditMode">
          {{ isEditMode ? "Save" : "Edit" }}
        </v-btn>
      </v-row>
      <v-row>
        <h3 class="mb-3">Name</h3>
      </v-row>
      <div>
        <v-form v-model="isValid">
          <v-row>
            <v-col>
              <v-row>
                <v-text-field
                  v-model="personInfo.namePrefix"
                  :disabled="!isEditMode" class="mr-1"
                  label="Prefix"
                  :rules="[v => (v || '' ).length <= 10 || 'Prefix is limited to 10 chars']"
                  outlined
                />
                <v-text-field
                  v-model="personInfo.firstName"
                  :disabled="!isEditMode" class="mr-1"
                  label="First Name*"
                  :rules="[(v) => !!v || 'First Name is required', (v) => !!v && v.length <= 50 || 'First Name is limited to 50 chars.']"
                  outlined
                />
                <v-text-field
                  v-model="personInfo.lastName"
                  :disabled="!isEditMode" class="mr-1"
                  label="Last Name*"
                  :rules="[(v) => !!v || 'Last Name is required', (v) => !!v && v.length <= 50 || 'Last Name is limited to 50 chars.']"
                  outlined
                />
                <v-text-field
                  v-model="personInfo.nameSuffix"
                  :disabled="!isEditMode" class="mr-1"
                  label="Suffix"
                  :rules="[v => (v || '' ).length <= 10 || 'Suffix is limited to 10 chars']"
                  outlined
                />
              </v-row>
              <v-row>
                <h3 class="mb-3">Contact Information</h3>
              </v-row>
              <v-row>
                <v-text-field
                  v-model="personInfo.email"
                  :disabled="!isEditMode" class="mr-1"
                  label="Email"
                  :rules="[v => (v || '' ).length <= 1024 || 'Email is limited to 1024 chars']"
                  outlined
                />
                <InternationalPhoneNumberInput
                  classNames="mr-1"
                  :phone="personInfo.phoneNumber"
                  :disabled="!isEditMode"
                  @changeData="(v) => personInfo.phoneNumber = v.number.e164"
                />
              </v-row>
              <v-row class="pa-0">
                <v-col>
                  <v-checkbox
                    v-model="personInfo.emailAddressVerified"
                    :disabled="!isEditMode"
                    label="Email verified"
                  />
                </v-col>
                <v-col>
                  <v-checkbox
                    :indeterminate="personInfo.communicationPreferenceEmail == null"
                    v-model="personInfo.communicationPreferenceEmail"
                    :disabled="!isEditMode || !personInfo.emailAddressVerified"
                    label="Communication preference: Email"
                    v-on:change="toggleCommunicationPreferenceEmailCheckbox"
                  />
                </v-col>
                <v-col>
                  <v-checkbox
                    v-model="personInfo.phoneNumberVerified"
                    :disabled="!isEditMode"
                    label="Phone number verified"
                  />
                </v-col>
                <v-col>
                  <v-checkbox
                    :indeterminate="personInfo.communicationPreferenceSms == null"
                    v-model="personInfo.communicationPreferenceSms"
                    :disabled="!isEditMode || !personInfo.phoneNumberVerified"
                    label="Communication preference: SMS"
                    v-on:change="toggleCommunicationPreferenceSmsCheckbox"
                  />
                </v-col>
                <v-col>
                  <v-checkbox
                    :color="personInfo.printPreferencesDigitalOnlyTitleCert  ? 'green' : 'red'"
                    v-model="personInfo.printPreferencesDigitalOnlyTitleCert"
                    :disabled="!isEditMode"
                  >
                  <template v-slot:label>
                    <v-icon 
                      :color="personInfo.printPreferencesDigitalOnlyTitleCert ? 'green' : 'red'"> 
                      mdi-leaf 
                    </v-icon>
                    <span :textColor="personInfo.printPreferencesDigitalOnlyTitleCert ? 'green' : 'red'">Title certificates: digital only</span>
                  </template>
                    </v-checkbox>
                </v-col>
              </v-row>
              <v-row>
                <CountryDropdown
                  :disabled="!isEditMode"
                  :selectedCountry="personInfo.country"
                  v-on:update="updateCountry"
                />
                <StateDropdown
                  v-if="personInfo.country === country_USA"
                  :classNames="'ml-1'"
                  required
                  :disabled="!isEditMode"
                  :selectedState="personInfo.stateAbbreviation"
                  v-on:update="personInfo.stateAbbreviation = $event"
                />
              </v-row>
              <v-row>
                <v-col>
                  <v-row>
                    <v-text-field
                      v-model="personInfo.street"
                      :disabled="!isEditMode" class="mr-1"
                      label="Address"
                      :rules="[v => (v || '' ).length <= 50 || 'Address is limited to 50 chars']"
                      outlined
                    />
                    <v-text-field
                      v-model="personInfo.city"
                      :disabled="!isEditMode"
                      label="City"
                      :rules="[v => (v || '' ).length <= 50 || 'City is limited to 50 chars']"
                      outlined
                    />
                  </v-row>
                </v-col>
              </v-row>
              <v-row>
                <v-text-field
                  v-model="personInfo.international"
                  :disabled="!isEditMode || personInfo.country === country_USA" class="mr-1"
                  label="International Area"
                  :rules="[v => (v || '' ).length <= 50 || 'International Area is limited to 50 chars']"
                  outlined
                />
                <v-text-field
                  v-model="personInfo.zip"
                  :disabled="!isEditMode"
                  label="Zip"
                  :rules="[v => (v || '' ).length <= 10 || 'Zip is limited to 10 chars']"
                  outlined
                />
              </v-row>
            </v-col>
          </v-row>
          <v-row>
            <h3>Affiliation Types</h3>
          </v-row>
          <v-row>
            <v-checkbox v-model="personInfo.isMember" class="mr-5" label="Member" :disabled="isCheckboxDisabled('member')" />
            <v-checkbox v-model="personInfo.isJudge" class="mr-5" label="Judge" :disabled="isCheckboxDisabled('judge')" />
            <v-checkbox v-model="personInfo.isJuniorHandler" class="mr-5" label="Junior Handler" :disabled="isCheckboxDisabled('juniorHandler')" />
            <v-checkbox v-model="personInfo.isNationalOfficer" class="mr-5" label="National Officer" :disabled="!isEditMode" />
            <v-checkbox v-model="personInfo.isNationalDirector" class="mr-5" label="National Director" :disabled="!isEditMode" />
            <v-checkbox v-model="personInfo.isAffiliateOfficer" class="mr-5" label="Affiliate Officer" :disabled="true" v-if="personId" />
          </v-row>
          <div v-if="personId" class="mt-5">
            <v-row>
              <h3 class="mb-3">NSF & Suspended Status Information</h3>
            </v-row>
            <v-row>
              <v-col>
                <v-row>
                  <span class="mr-5">
                    <v-checkbox v-model="personInfo.nsfStatus" label="NSF" :disabled="!isEditMode" />
                  </span>
                  <v-checkbox v-model="personInfo.isSuspended" label="Suspended" :disabled="!isEditMode" />
                </v-row>
                <v-row>
                  <v-text-field
                    v-model="personInfo.nsfAmount"
                    :disabled="!isEditMode" class="mr-1"
                    label="NSF Amount" 
                    outlined
                  />
                  <v-text-field
                    v-model="personInfo.nsfReason"
                    :disabled="!isEditMode" class="mr-1"
                    :rules="[v => (v || '' ).length <= 50 || 'Reason is limited to 50 chars']"
                    label="NSF Reason"
                    outlined
                  />
                </v-row>
                <v-row>
                  <DatepickerFormatted 
                    :disabled="!isEditMode"
                    :date="personInfo.nsfStartDate"
                    label="NSF Start Date" class="mr-1"
                    v-on:updateDates="updateDates($event, 'nsfStartDate')" />
                  <DatepickerFormatted 
                    :disabled="!isEditMode"
                    :date="personInfo.nsfEndDate"
                    label="NSF End Date"
                    v-on:updateDates="updateDates($event, 'nsfEndDate')" />
                </v-row>
              </v-col>
            </v-row>
          </div>
        </v-form>
      </div>
    </div>
    <div class="mt-3">
      <v-divider class="my-3" />
      <v-row class="pt-0 mt-0">
        <v-col>
          <h3>Notes</h3>
        </v-col>
      </v-row>
      <v-row>
        <v-textarea
          disabled
          @input="updateText('notes', $event)"
          :value="personInfo.notes"
          outlined
          rows="1"
          auto-grow
        />
      </v-row>
    </div>
    <v-snackbar v-model="showInfoMessage" title color="green">
      {{ infoMessage }}
      <template v-slot:action="{ attrs }">
      <v-btn color="primary" text v-bind="attrs" @click="showInfoMessage = false">
          Close
      </v-btn>
      </template>
    </v-snackbar>
    <v-snackbar v-model="showErrorMessage" title color="red">
      {{ errorMessage }}
      <template v-slot:action="{ attrs }">
      <v-btn color="primary" text v-bind="attrs" @click="showErrorMessage = false">
          Close
      </v-btn>
      </template>
    </v-snackbar>   
  </v-card>
</template>

<script lang="ts">
import Vue from 'vue'
import CountryDropdown from '@/components/common/inputs/CountryDropdown.vue';
import StateDropdown from '@/components/common/inputs/StateDropdown.vue';
import DatepickerFormatted from "../../common/inputs/DatepickerFormatted.vue";
import InternationalPhoneNumberInput from '@/components/common/inputs/InternationalPhoneNumberInput.vue';
import normalizeAddressRegion from '@/helpers/normalize-address-region';

import peopleService from "@/services/admin/peopleService";

import { Messages, Countries } from "@/constants";
import { AdminPersonInfo, AdminPersonNotes } from '@/types/admin/Person';

export default Vue.extend({
  name: "GeneralPersonDetails",
  components: {
    CountryDropdown,
    StateDropdown,
    DatepickerFormatted,
    InternationalPhoneNumberInput,
  },
  props: {
      personId: { type: Number },
  },
  data: function () {
      return {
          personInfo: {
              memberInfo: {},
              country: Countries.USA,
              stateAbbreviation: "",
              international: ""
          } as AdminPersonInfo,
          isEditMode: false,
          isMemberEditMode: false,
          showValidationDialog: false,
          validationResult: {
              warnings: [],
              errors: []
          } as any,
          isValid: false,
          personNote: {} as AdminPersonNotes,
          errorMessage: '',
          infoMessage: '',
          showErrorMessage: false,
          showInfoMessage: false,
          
          country_USA: Countries.USA,   
          currentCommPrefEmailCheckboxIdx: 0,
          currentCommPrefSmsCheckboxIdx: 0,
          checkboxStates: [null, true, false],
      }
  },
  created: function () {
      if (this.personId) {
          this.personNote.personId = this.personId;
          this.personNote.newNote = "";
          this.personNote.notes = "";
          this.reloadData();
      }
      else {
          this.isEditMode = true;               
      }
  },
  watch: {
      personId: function (newVal: String) {
          peopleService
              .getPersonById(this.personId)
              .then((res) => {
                  this.personInfo = res;
                  this.normalizeAddressRegion();
              });
      },
  },
  methods: {
      reloadData: function () {
          if (this.personId) {
              peopleService
                  .getPersonById(this.personId)
                  .then((res) => {
                      this.personInfo = res;
                      this.normalizeAddressRegion();
                      this.currentCommPrefEmailCheckboxIdx = this.checkboxStates.indexOf(this.personInfo.communicationPreferenceEmail);
                      this.currentCommPrefSmsCheckboxIdx = this.checkboxStates.indexOf(this.personInfo.communicationPreferenceSms);
                  });
          }

          this.showValidationDialog = false;
          this.errorMessage = '';
          this.showErrorMessage = false;
          this.validationResult = {};
      },
      saveChanges: function () {
          this.normalizeAddressRegion();
          this.errorMessage = "";
          this.showValidationDialog = false;
          this.showErrorMessage = false;
          this.$store.dispatch("changeLoaderStatus", true);

          peopleService
              .editPersonById(this.personInfo)
              .then((res) => {
                  if (res.success) {
                      if (res.id) {
                          if (this.personId) {
                              this.reloadData();
                              window.location.reload();
                          }
                          else {
                              this.reloadData();
                              this.$router.push({ path: `/admin/people/${res.id}` }).catch(this.$handleNavigationError);
                          }
                      }
                      
                      this.isEditMode = false;
                      this.infoMessage = Messages.SaveSuccessful;
                      this.showInfoMessage = true;
                  } else {              
                      this.errorMessage = Messages.SaveError + " " + res.messages.join(', ');
                      this.showErrorMessage = true;    
                  }})
                  .catch((e) => {                  
                      this.errorMessage = Messages.SaveError;
                      this.showErrorMessage = true;
                  })
              .finally(() => this.$store.dispatch("changeLoaderStatus", false));
      },
      updateDates: function (val: any, key: string) {
          this.$data.personInfo[key] = val?.date;
          this.$data.personInfo = Object.assign({}, this.$data.personInfo);
      },
      updateText: function (key: string, event: string) {
          this.$data.personInfo[key] = event;
      },
      togglePersonEditMode: function () {
          if (this.isEditMode) {
              this.validateAndConfirmSavingChanges();
          }
          this.isEditMode = !this.isEditMode;
      },
      addNote() {
          if (this.personNote.newNote) {
              peopleService
                  .editPersonAddNote(this.personNote)
                  .then((res) => {
                      let personNotes = res as AdminPersonNotes;
                      this.personInfo.notes = personNotes.notes;
                      this.personNote.newNote = "";
                      this.personNote.notes = "";
                  })
                  .catch(this.$handleError)
                  .finally(() => this.$store.dispatch("changeLoaderStatus", false));
          }
      },
      validateAndConfirmSavingChanges: function () {
          this.normalizeAddressRegion();
          if (this.validateFields()) {
              peopleService
                  .validatePersonEdit(this.personInfo)
                  .then((res: any) => {
                      this.validationResult = res;
                      if (this.validationResult.errors.length > 0 || this.validationResult.warnings.length > 0) {
                          this.showValidationDialog = true;
                      }
                      else {
                          this.saveChanges();
                          this.validationResult = {};
                      }
                  })
          }
          else {
              this.isEditMode = false; //this will toggle it to true
          }
      },
      validateFields() {
          this.errorMessage = "";
          if (!this.personInfo) {
              this.errorMessage = "Missing required fields";
              return false;
          }
          if (this.personInfo.firstName == null || this.personInfo.lastName == null) {
              this.errorMessage = "First and Last Name are required.";
              return false;
          }
          if (this.personInfo.city) {
              if (this.personInfo.city.length > 50) {
                  this.errorMessage = "City is limited to 50 chars.";
                  return false;
              }
          }
          if (this.personInfo.country) {
              if (this.personInfo.country.length > 50)
              {
                  this.errorMessage = "Country is limited to 50 chars.";
                  return false;
              }
          }
          if (this.personInfo.international) {
              if (this.personInfo.international.length > 50)
              {
                  this.errorMessage = "International Area is limited to 50 chars.";
                  return false;
              }
          }
          if (this.personInfo.street) {
              if (this.personInfo.street.length > 50)
              {
                  this.errorMessage = "Address is limited to 50 chars.";
                  return false;
              }
          }
          if (this.personInfo.namePrefix) {
              if (this.personInfo.namePrefix.length > 10) {
                  this.errorMessage = "Prefix is limited to 10 chars.";
                  return false;
              }
          }
          if (this.personInfo.nameSuffix) {
              if (this.personInfo.nameSuffix.length > 10) {
                  this.errorMessage = "Suffix is limited to 10 chars.";
                  return false;
              }
          }
          if (this.personInfo.phoneNumber) {
              if (this.personInfo.phoneNumber.length > 50) {
                  this.errorMessage = "Phone is limited to 50 chars.";
                  return false;
              }
          }
          if (this.personInfo.zip) {
              if (this.personInfo.zip.length > 10) {
                  this.errorMessage = "Zip is limited to 10 chars.";
                  return false;
              }
          }
          if (this.personInfo.email) {
              if (this.personInfo.email.length > 1024) {
                  this.errorMessage = "Email is limited to 1024 chars.";
                  return false;
              }
          }
          if (this.personInfo.stateAbbreviation) {
              if (this.personInfo.stateAbbreviation.length > 2) {
                  this.errorMessage = "State Abbreviation is limited to 2 chars.";
                  return false;
              }
          }
          if (this.personInfo.nsfReason) {
              if (this.personInfo.nsfReason.length > 50) {
                  this.errorMessage = "NSF Reason is limited to 50 chars.";
                  return false;
              }
          }

          return true;
      },
      updateCountry: function (country: string) {
          this.personInfo.country = country;
          this.normalizeAddressRegion();
      },
      normalizeAddressRegion: function () {
          const region = normalizeAddressRegion(
              this.personInfo.country,
              this.personInfo.stateAbbreviation,
              this.personInfo.international
          );
          this.$set(this.personInfo, "stateAbbreviation", region.state);
          this.$set(this.personInfo, "international", region.international);
      },
      isCheckboxDisabled: function (key: string) {
          if (!this.isEditMode) {
              return true;
          }
          if (key == "member") {
              if (!this.personInfo.isMember) {
                  return false;
              }
              if (!this.personInfo.memberInfo) {
                  return false;

              }
              if (!this.personInfo.memberInfo.memberId) {
                  return false;
              }
          }
          else if (key == "judge") {
              if (!this.personInfo.isJudge) {
                  return false;
              }
              if (this.personInfo.judgeId == null) {
                  return false;
              }
          }
          else if (key == "juniorHandler") {
              if (!this.personInfo.isJuniorHandler) {
                  return false;
              }
              if (!this.personInfo.juniorHandlerId) {
                  return false;
              }
          }

          return true;
      },
      toggleCommunicationPreferenceEmailCheckbox: function () {
        var next = ++this.currentCommPrefEmailCheckboxIdx % this.checkboxStates.length;
        this.currentCommPrefEmailCheckboxIdx = next;
        this.personInfo.communicationPreferenceEmail = this.checkboxStates[next];
      },
        toggleCommunicationPreferenceSmsCheckbox: function () {
        var next = ++this.currentCommPrefSmsCheckboxIdx % this.checkboxStates.length;
        this.currentCommPrefSmsCheckboxIdx = next;
        this.personInfo.communicationPreferenceSms = this.checkboxStates[next];
      },
  },
})
</script>
